import mixpanel from "mixpanel-browser";
import { getStoreWorkspace } from "@shared/common/utils";
import { WorkspaceType } from "@shared/types";
import { analyticsPrivacyHooks, prepareAnalyticsUrls } from "./urlPrivacy";
import {
  getAnalyticsSurface,
  getOnboardingJourneyContext,
  getTelegramClient,
  isOnboardingEvent,
} from "./journey";
import {
  bindVerificationUser,
  clearVerificationEvents,
  deferVerificationEvent,
  flushVerificationEvents,
} from "./verificationBuffer";

export {
  clearVerificationEvents,
  replaceVerificationChallenge,
} from "./verificationBuffer";

let initialized = false;
let trackingAllowed = false;
let identifiedUser: string | undefined;
let verificationEventContext:
  | { time: number; $current_url: string }
  | undefined;

export const initializeAnalytics = (token: string | undefined): void => {
  if (!token || initialized || import.meta.env.VITE_APP_MOCK_API === "true")
    return;
  try {
    mixpanel.init(token, {
      debug: false,
      persistence: "cookie",
      save_referrer: false,
      loaded: prepareAnalyticsUrls,
      batch_requests: true,
      autocapture: false,
      track_pageview: false,
      record_sessions_percent: 0,
      hooks: analyticsPrivacyHooks,
    });
    mixpanel.unregister("onboarding_experiment");
    mixpanel.unregister("onboarding_variant");
    registerSurface();
    initialized = true;
  } catch {
    initialized = false;
  }
};

/** Every event says where it ran: browser, the app's WebView, or the
    Telegram Mini App (`surface`, a super-property), and in the Mini App
    which Telegram client. */
const registerSurface = (): void => {
  try {
    mixpanel.register({
      surface: getAnalyticsSurface(),
      ...getTelegramClient(),
    });
  } catch {
    return;
  }
};

const definedOnly = (
  properties: Record<string, unknown>
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(properties).filter(([, value]) => value !== undefined)
  );

const canTrack = (): boolean => {
  try {
    return (
      initialized &&
      trackingAllowed &&
      getStoreWorkspace() !== WorkspaceType.DEMO &&
      !mixpanel.has_opted_out_tracking()
    );
  } catch {
    return false;
  }
};

export const resetAnalyticsIdentity = (): void => {
  identifiedUser = undefined;
  trackingAllowed = false;
  try {
    if (initialized) mixpanel.reset();
  } catch {
    return;
  }
};

export const setAnalyticsIdentity = (
  userId: string | undefined,
  allowed: boolean
): void => {
  const persistedUser = getPersistedUser();
  if (
    (identifiedUser && identifiedUser !== userId) ||
    (userId && persistedUser && persistedUser !== userId)
  )
    resetAnalyticsIdentity();
  trackingAllowed = allowed && !!userId;
  // Again here: the Mini App check may resolve after initialization.
  if (initialized) registerSurface();
  if (userId) identifyAnalyticsUser(userId);
  flushVerificationEvents(userId, canTrack() && identifiedUser === userId);
};

const getPersistedUser = (): string | undefined => {
  try {
    const userId = initialized && mixpanel.get_property("$user_id");
    return userId ? String(userId) : undefined;
  } catch {
    return undefined;
  }
};

export const identifyAnalyticsUser = (userId: string): void => {
  if (!canTrack() || identifiedUser === userId) return;
  try {
    mixpanel.identify(userId);
    identifiedUser = userId;
  } catch {
    trackingAllowed = false;
  }
};

export const trackEvent = (
  name: string,
  properties: Record<string, unknown>,
  sendImmediately = false
): void => {
  if (!canTrack()) return;
  try {
    // Onboarding events also carry the current attempt (method, mode,
    // path), unless they set their own.
    const payload: Record<string, unknown> = isOnboardingEvent(name)
      ? { ...getOnboardingJourneyContext(), ...definedOnly(properties) }
      : { ...properties };
    Object.assign(payload, verificationEventContext);
    if (sendImmediately)
      mixpanel.track(name, payload, {
        send_immediately: true,
        transport: "sendBeacon",
      });
    else mixpanel.track(name, payload);
  } catch {
    return;
  }
};

export const queueVerificationAnalytics = (
  challenge: string,
  event: "view" | "resend",
  send: () => void
): void => {
  try {
    if (getStoreWorkspace() === WorkspaceType.DEMO)
      return clearVerificationEvents();
    if (initialized && mixpanel.has_opted_out_tracking())
      return clearVerificationEvents();
    const context = {
      time: Math.floor(Date.now() / 1000),
      $current_url: window.location.origin + window.location.pathname,
    };
    deferVerificationEvent(challenge, event, () => {
      verificationEventContext = context;
      try {
        send();
      } finally {
        verificationEventContext = undefined;
      }
    });
  } catch {
    return;
  }
};

export const completeVerificationAnalytics = (
  challenge: string,
  userId: string
): void => {
  bindVerificationUser(challenge, userId);
  if (identifiedUser === userId) flushVerificationEvents(userId, canTrack());
};
